import * as fs from "node:fs";
import * as path from "node:path";
import { createRequire } from "node:module";

import { EXIT_OK, type CommandOutput } from "../common.js";
import { candidateEnvPaths } from "../infra/env.js";
import { resolveOutputMode } from "../infra/script-output-store.js";
import { loadLlmConfig } from "../llm/config.js";
import { ModelRouter } from "../llm/router.js";

const require = createRequire(import.meta.url);

function moduleAvailable(name: string): boolean {
  try {
    require.resolve(name);
    return true;
  } catch {
    return false;
  }
}

// Read-only probe — never creates or modifies anything. doctor must stay
// side-effect-free so `--json` consumers can re-run it idempotently.
function probeLocalOutputDir(dir: string): {
  writable: boolean;
  hasScript: boolean;
} {
  let writable = false;
  try {
    const stat = fs.statSync(dir);
    if (stat.isDirectory()) {
      fs.accessSync(dir, fs.constants.W_OK);
      writable = true;
    }
  } catch {
    writable = false;
  }
  let hasScript = false;
  try {
    hasScript = fs.statSync(path.join(dir, "script.json")).isFile();
  } catch {
    hasScript = false;
  }
  return { writable, hasScript };
}

// Default output path mirrors LocalScriptOutputStore.fromEnv()'s fallback:
// sibling of the default workspace dir, which lives at <cwd>/workspace, so
// the default output is <cwd>/output. The project-template lays both out
// at the same level under each stage dir.
function defaultLocalOutputDir(): string {
  return path.join(path.dirname(path.resolve("workspace")), "output");
}

export function commandDoctor(opts: Record<string, unknown> = {}): [CommandOutput, number] {
  const envPaths = candidateEnvPaths();
  const existingEnvPaths: string[] = [];
  const seen = new Set<string>();
  for (const p of envPaths) {
    try {
      if (!fs.existsSync(p) || !fs.statSync(p).isFile()) continue;
      const resolved = fs.realpathSync(p);
      if (seen.has(resolved)) continue;
      seen.add(resolved);
      existingEnvPaths.push(p);
    } catch {
      // ignore individual entries that cannot be inspected
    }
  }

  const explicitLlmConfig = typeof opts["llm_config"] === "string" ? opts["llm_config"].trim() : "";
  const llmConfig = loadLlmConfig(explicitLlmConfig || undefined);
  const llmRouter = new ModelRouter(llmConfig);
  const llmRoutes = llmRouter.routes();
  const credentialChecks = Object.entries(llmConfig.providers).map(([alias, provider]) => ({
    alias,
    configured: Boolean((process.env[provider.apiKeyEnv] ?? "").trim()),
    env: provider.apiKeyEnv,
  }));
  // openai-compatible providers need an explicit endpoint (the built-in `gw`
  // reads PROJECT_LITELLM_GATEWAY_BASE_URL). CommandOutput the resolved host so a
  // missing / mis-pointed gateway is visible — a null baseUrl here is why
  // ingest normalization fails at connection time. anthropic/gemini use their
  // SDK default endpoint, so a null baseUrl there is expected and not reported.
  const endpointChecks = Object.entries(llmConfig.providers)
    .filter(([, provider]) => provider.protocol === "openai-compatible")
    .map(([alias, provider]) => {
      let host = "not set";
      if (provider.baseUrl) {
        try { host = new URL(provider.baseUrl).host; } catch { host = provider.baseUrl; }
      }
      return { alias, host };
    });

  // doctor reports the AMBIENT binding (env + project-group inference), so it
  // resolves with no per-call flags. mode is null when the env is unbound or
  // holds a typo'd SCRIPTCTL_OUTPUT_MODE — both surface as "not configured".
  const { mode } = resolveOutputMode();
  const localDirRaw = (process.env.SCRIPTCTL_OUTPUT_DIR ?? "").trim() || defaultLocalOutputDir();
  const localProbe = mode === "local" ? probeLocalOutputDir(localDirRaw) : null;

  const gatewayUrlConfigured = Boolean(process.env.AWB_BASE_URL);
  const gatewayAccessConfigured = Boolean(process.env.LINGJING_AWB_ACCESS_KEY);
  const projectGroupConfigured = Boolean(process.env.SANDBOX_PROJECT_GROUP_NO);

  const dependencies: Record<string, boolean> = {
    "@anthropic-ai/sdk": moduleAvailable("@anthropic-ai/sdk"),
    "@google/genai": moduleAvailable("@google/genai"),
    jszip: moduleAvailable("jszip"),
    "fast-xml-parser": moduleAvailable("fast-xml-parser"),
  };
  const missingDeps = Object.entries(dependencies)
    .filter(([, ok]) => !ok)
    .map(([name]) => name);

  const nodeVersion = process.versions.node;
  const envFileLine = `env_file: ${existingEnvPaths[0] ?? "missing"}`;
  const fmtCheck = (ok: boolean): string => (ok ? "configured" : "not configured");
  // Workspace storage is always the local filesystem. Stable since pre-0.7.0;
  // scripts that grep `storage:` keep working.
  const storageLine = "storage: local-filesystem";

  const result = [
    `node: ${nodeVersion}`,
    storageLine,
    envFileLine,
    `llm config: ${llmConfig.source}`,
    `llm routes: ${llmRoutes.length}`,
    ...credentialChecks.map((check) => `llm provider ${check.alias}: ${fmtCheck(check.configured)}`),
    ...endpointChecks.map((check) => `llm provider ${check.alias} endpoint: ${check.host}`),
  ];
  // Doctor result lines vary by where the published script lands, but the
  // word "mode" / "local" / "remote" is deliberately absent: upper layers
  // must not know there are two backends.
  if (mode === null) {
    // Unbound: no per-call flag and no env binding. Stay neutral — naming the
    // backend kinds (or the env var) here would leak the mode concept.
    result.push(`output target: not configured`);
  } else if (mode === "local" && localProbe) {
    result.push(`output writable: ${fmtCheck(localProbe.writable)}`);
    result.push(`output snapshot: ${localProbe.hasScript ? "present" : "absent"}`);
  } else {
    result.push(`gateway endpoint: ${fmtCheck(gatewayUrlConfigured)}`);
    result.push(`gateway access credential: ${fmtCheck(gatewayAccessConfigured)}`);
    result.push(`gateway project identifier: ${fmtCheck(projectGroupConfigured)}`);
  }
  for (const [name, ok] of Object.entries(dependencies)) {
    result.push(`dependency ${name}: ${ok ? "ok" : "missing"}`);
  }

  const nextSteps: string[] = [];
  if (missingDeps.length > 0) {
    nextSteps.push("Install missing CLI dependencies (see above).");
  }
  if (credentialChecks.some((check) => !check.configured)) {
    nextSteps.push("Configure every credential referenced by the selected LLM configuration.");
  }
  if (endpointChecks.some((check) => check.host === "not set")) {
    nextSteps.push("An openai-compatible provider has no endpoint — set PROJECT_LITELLM_GATEWAY_BASE_URL (built-in gateway) or the provider's baseUrl.");
  }
  // A typo'd SCRIPTCTL_OUTPUT_MODE resolves to mode=null (unbound), same as
  // unset — reported as "not configured" below. We deliberately don't name the
  // env var here: that would leak the mode concept to the upper layer.
  if (mode === null) {
    nextSteps.push("Configure an output target before running `publish` / script CRUD.");
  } else if (mode === "local") {
    if (localProbe && !localProbe.writable) {
      nextSteps.push("Output target is not writable; check directory permissions.");
    }
  } else if (!gatewayUrlConfigured || !gatewayAccessConfigured || !projectGroupConfigured) {
    nextSteps.push("Configure gateway credentials before running `publish` / script CRUD against the remote project.");
  }
  if (nextSteps.length === 0) {
    nextSteps.push("Environment looks ready.");
  }

  const [major, minor] = nodeVersion.split(".").map((n) => parseInt(n, 10));
  const nodeOk = (major ?? 0) > 20 || ((major ?? 0) === 20 && (minor ?? 0) >= 0);

  // checks.output exposes only the relevant sub-keys; presence of `endpoint`
  // (remote-style) vs `dir` (local-style) is the implicit discriminator.
  // No `mode` field — upper layers must not branch on a mode concept.
  const outputChecks: Record<string, unknown> =
    mode === null
      ? { configured: false }
      : mode === "local"
        ? {
            dir: {
              ok: Boolean(localProbe?.writable),
              writable: Boolean(localProbe?.writable),
              hasScript: Boolean(localProbe?.hasScript),
            },
          }
        : {
            endpoint: { ok: gatewayUrlConfigured },
            access: { ok: gatewayAccessConfigured },
            project: { ok: projectGroupConfigured },
          };

  const checks: Record<string, unknown> = {
    node: { version: nodeVersion, ok: nodeOk },
    storage: { backend: "local-filesystem", ok: true },
    env: {
      ok: existingEnvPaths.length > 0,
      paths: existingEnvPaths,
    },
    llm: {
      config_source: llmConfig.source,
      ...(llmConfig.sourcePath ? { config_path: llmConfig.sourcePath } : {}),
      providers: credentialChecks.map((check) => ({
        alias: check.alias,
        ok: check.configured,
        credential_env: check.env,
        ...(endpointChecks.find((e) => e.alias === check.alias)
          ? { endpoint: endpointChecks.find((e) => e.alias === check.alias)!.host }
          : {}),
      })),
      profiles: Object.keys(llmConfig.models),
      routes: llmRoutes.map((route) => ({
        task: route.task,
        profile: route.profile,
        provider: route.provider,
        protocol: route.protocol,
        model: route.model,
      })),
    },
    output: outputChecks,
    dependencies,
  };

  const report: CommandOutput = {
    text: [...result, ...nextSteps].join("\n"),
    data: { checks },
  };
  return [report, EXIT_OK];
}
